import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addDays } from 'date-fns/esm';

import Booking from '../../../core/types/model/Booking';
import EntityName from '../../../core/enums/EntityName';
import { RootState } from '../../../core/state/state';
import { selectProjectSettings } from '../../../core/state/bookingProjectSettings/selectors';
import { getSelectedProjectId } from '../../../core/state/bookings/selectors';
import { generateEntityFetchListAsyncAction } from '../../../core/state/entity/generateEntityAsyncActions';
import { generateTimeSlotDefinitions } from '../helpers';
import { formatDate } from '../../../core/utils/date';
import { DATE_FORMAT_ISO } from '../../../core/constants/date';

import DayColumn from './day-column/DayColumn';
import BookingDetailsModal from '../modals/booking-details/BookingDetailsModal';
import { isMobileScreenSize } from '../../../core/utils/isMobileScreenSize';

import './Calendar.scss';

interface Props {
  isPrinting?: boolean;
}

const Calendar: React.FC<Props> = ({ isPrinting }) => {
  const dispatch = useDispatch();

  const projectId = useSelector(getSelectedProjectId) as number;
  const projectSettings = useSelector(selectProjectSettings);
  const selectedBooking = useSelector((state: RootState) => state.bookings.selectedBooking);
  const monday = useSelector((state: RootState) => state.bookings.selectedDate);
  const [updateBlockedSlots, setUpdateBLockedSlots] = React.useState(false);
  const isMobileScreen = isMobileScreenSize();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchBookings = useMemo(() => generateEntityFetchListAsyncAction<Booking>(EntityName.Booking), [updateBlockedSlots]);

  useEffect(() => {
    if (projectId) {
      const startDate = formatDate(monday, DATE_FORMAT_ISO);
      const endDate = formatDate(addDays(monday, 6), DATE_FORMAT_ISO);
      dispatch(fetchBookings({ projectId, startDate, endDate }));
      setUpdateBLockedSlots(false);
    }
  }, [dispatch, fetchBookings, monday, projectId]);

  const days = useMemo(() => {
    const d = [] as Date[];
    for (let i = 0; i < 6; i += 1) {
      d.push(addDays(monday, i));
    }
    return d;
  }, [monday]);

  const {
    bookingProjectSettings: { operatingHoursFrom, operatingHoursTo }
  } = projectSettings;
  const timeSlots = generateTimeSlotDefinitions(operatingHoursFrom, operatingHoursTo);

  return (
    <div className={`calendar ${isMobileScreen && 'calendar--mobile'}`}>
      <div className="calendar__time">
        {timeSlots.map((ts) => (
          <div key={ts.index} className={`calendar__time-label ${ts.rowSpan === 1 ? 'calendar__time-label--short' : ''}`}>
            {ts.onHour && <span>{ts.startTime}</span>}
          </div>
        ))}
      </div>
      <div className="calendar__grid">
        {days.map((date) => (
          <DayColumn key={date.toUTCString()} date={date} timeSlots={timeSlots} projectSettings={projectSettings} isPrinting={isPrinting} />
        ))}
      </div>
      {selectedBooking && <BookingDetailsModal fetchBookings={() => setUpdateBLockedSlots(true)} booking={selectedBooking} />}
    </div>
  );
};

export default Calendar;
